/**
 * What is wrong with this process, and where.
 *
 * The designer used to learn about a problem from a toast that said "1 arrow(s)
 * have no record match — open each one and say which columns join", and then
 * had to open every arrow to find it. A finding here names the thing, says what
 * to do about it, and SELECTS it on the canvas when clicked; the canvas marks
 * the same node or arrow with a dot in the same colour. Nothing has to be
 * hunted for.
 */

import { useState } from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronUp, Info, XCircle } from 'lucide-react'

import type { FlowFinding } from '@/api/processflow'

interface Props {
  findings: FlowFinding[]
  selection: { kind: 'node' | 'edge'; id: string } | null
  onSelect: (sel: { kind: 'node' | 'edge'; id: string } | null) => void
}

const TONE: Record<string, { color: string; icon: React.ReactNode; word: string }> = {
  error: {
    color: 'var(--color-error, #dc2626)',
    icon: <XCircle size={13} />, word: 'Must fix',
  },
  warning: {
    color: 'var(--color-warning, #b45309)',
    icon: <AlertTriangle size={13} />, word: 'Leaves a hole',
  },
  info: {
    color: 'var(--color-text-secondary)',
    icon: <Info size={13} />, word: 'Worth a look',
  },
}

export default function IssuesPanel({ findings, selection, onSelect }: Props) {
  const [open, setOpen] = useState(true)
  const errors = findings.filter((f) => f.severity === 'error').length
  const warnings = findings.filter((f) => f.severity === 'warning').length

  return (
    <div style={{
      borderTop: '1px solid var(--color-border)',
      background: 'var(--color-bg-secondary)',
      maxHeight: open ? '38vh' : undefined, display: 'flex', flexDirection: 'column',
    }}>
      <button type="button" onClick={() => setOpen((o) => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', cursor: 'pointer',
        padding: '7px 12px', background: 'transparent', border: 0,
        color: 'var(--color-text)', fontSize: '0.8425rem', fontWeight: 600,
      }}>
        {findings.length === 0
          ? <CheckCircle2 size={14} style={{ color: 'var(--color-success, #16a34a)' }} />
          : <AlertTriangle size={14} style={{
            color: errors ? 'var(--color-error, #dc2626)' : 'var(--color-warning, #b45309)',
          }} />}
        {findings.length === 0
          ? 'Nothing to fix on this process'
          : `${findings.length} thing${findings.length === 1 ? '' : 's'} to look at`}
        {errors > 0 && <Pill tone="error">{errors} must fix</Pill>}
        {warnings > 0 && <Pill tone="warning">{warnings} leaves a hole</Pill>}
        <span style={{ flex: 1 }} />
        {open ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
      </button>

      {open && findings.length > 0 && (
        <div style={{ overflowY: 'auto', padding: '0 8px 10px' }}>
          {findings.map((f, i) => {
            const tone = TONE[f.severity] || TONE.info
            const target = f.edge_id
              ? { kind: 'edge' as const, id: f.edge_id }
              : f.node_id ? { kind: 'node' as const, id: f.node_id } : null
            const active = !!target && selection?.kind === target.kind
              && selection.id === target.id
            return (
              <button
                key={`${f.code}-${i}`}
                type="button"
                onClick={() => onSelect(target)}
                disabled={!target}
                style={{
                  display: 'flex', gap: 8, width: '100%', textAlign: 'left',
                  padding: '7px 9px', marginBottom: 4, borderRadius: 7,
                  cursor: target ? 'pointer' : 'default',
                  border: `1px solid ${active ? tone.color : 'var(--color-border)'}`,
                  background: active
                    ? 'var(--color-accent-weak, rgba(0,97,208,0.08))'
                    : 'var(--color-input-bg, #fff)',
                  color: 'var(--color-text)',
                }}
              >
                <span style={{ color: tone.color, marginTop: 1, flexShrink: 0 }}>{tone.icon}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '0.8325rem', fontWeight: 600 }}>
                    {f.message}
                  </span>
                  <span style={{
                    display: 'block', fontSize: '0.8125rem', lineHeight: 1.45,
                    color: 'var(--color-text-secondary)', marginTop: 2,
                  }}>
                    {f.fix}
                    {target && (
                      <em style={{ color: tone.color, fontStyle: 'normal', fontWeight: 600 }}>
                        {' '}· click to open it
                      </em>
                    )}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function Pill({ tone, children }: { tone: 'error' | 'warning'; children: React.ReactNode }) {
  const color = TONE[tone].color
  return (
    <span style={{
      fontSize: '0.8125rem', fontWeight: 700, padding: '1px 6px', borderRadius: 20,
      color, border: `1px solid ${color}`, background: 'transparent',
    }}>
      {children}
    </span>
  )
}
